import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  RefreshCw,
  AlertCircle,
  Database,
  TrendingUp,
  TrendingDown,
  BarChart3,
  ExternalLink,
  Download,
  Calendar,
  SearchCheck,
  Wrench,
  Settings,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  X,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  getETFVolume,
  syncETFData,
  backfillETF,
  getBackfillStatus,
  getETFDateGaps,
  getETFWatchlist,
  addETFToWatchlist,
  updateETFWatchlistItem,
  removeETFFromWatchlist,
} from '../api/client'

// Color palette for new ETFs
const COLOR_PALETTE = [
  '#F59E0B', '#3B82F6', '#10B981', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
  '#F97316', '#6366F1', '#14B8A6', '#E11D48',
]

interface WatchlistItem {
  sec_code: string
  sec_name: string
  color: string
  sort_order: number
  enabled: number
  created_at: string
}

interface ETFRecord {
  stat_date: string
  sec_code: string
  sec_name: string
  tot_vol: number
  etf_type: string
}

interface ChartDataPoint {
  date: string
  [code: string]: string | number
}

type DateRange = '1m' | '3m' | '6m' | '1y' | 'all'

function formatYi(val: number): string {
  return val.toFixed(2) + ' 亿'
}

function getStartDateForRange(range: DateRange): string | undefined {
  if (range === 'all') return undefined
  const now = new Date()
  const daysMap: Record<string, number> = { '1m': 30, '3m': 90, '6m': 180, '1y': 365 }
  now.setDate(now.getDate() - daysMap[range])
  return now.toISOString().slice(0, 10)
}

export default function ETFVolume() {
  // Watchlist state
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([])
  const [watchlistLoading, setWatchlistLoading] = useState(true)
  const [showManager, setShowManager] = useState(false)
  const [newCode, setNewCode] = useState('')
  const [newName, setNewName] = useState('')
  const [addingCode, setAddingCode] = useState(false)
  const [addResult, setAddResult] = useState<{ type: 'ok' | 'warn'; text: string } | null>(null)
  const [colorPickerCode, setColorPickerCode] = useState<string | null>(null)

  // Data state (existing)
  const [records, setRecords] = useState<ETFRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState<DateRange>('all')
  const [hiddenLines, setHiddenLines] = useState<Set<string>>(new Set())
  const hiddenLinesInitialized = useRef(false)

  // Backfill state
  const [backfilling, setBackfilling] = useState(false)
  const [backfillProgress, setBackfillProgress] = useState<{ progress: number; total: number; message: string } | null>(null)
  const backfillTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [showBackfillPanel, setShowBackfillPanel] = useState(false)
  const [backfillStartDate, setBackfillStartDate] = useState('2025-01-01')
  const [backfillEndDate, setBackfillEndDate] = useState(new Date().toISOString().slice(0, 10))
  const [backfillForce, setBackfillForce] = useState(false)

  // Gap detection state
  interface DateGap { gap_start: string; gap_end: string; gap_days: number }
  const [gaps, setGaps] = useState<DateGap[] | null>(null)
  const [gapChecking, setGapChecking] = useState(false)
  const [gapFixing, setGapFixing] = useState(false)

  // --- Derived config from watchlist ---
  const etfConfig: Record<string, { name: string; color: string }> = {}
  const etfCodes: string[] = []
  for (const item of watchlist) {
    etfConfig[item.sec_code] = { name: item.sec_name, color: item.color }
    etfCodes.push(item.sec_code)
  }

  // --- Load watchlist ---
  const fetchWatchlist = useCallback(async () => {
    try {
      setWatchlistLoading(true)
      const resp = await getETFWatchlist()
      if (resp.success && resp.data) {
        setWatchlist(resp.data)
      }
    } catch {
      // silently fail, watchlist will be empty
    } finally {
      setWatchlistLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchWatchlist()
  }, [fetchWatchlist])

  // Initialize hiddenLines from watchlist enabled state (once)
  useEffect(() => {
    if (watchlist.length > 0 && !hiddenLinesInitialized.current) {
      const disabled = new Set<string>()
      for (const item of watchlist) {
        if (item.enabled === 0) {
          disabled.add(item.sec_code)
        }
      }
      setHiddenLines(disabled)
      hiddenLinesInitialized.current = true
    }
  }, [watchlist])

  // --- Load volume data ---
  const fetchData = useCallback(async (range: DateRange, isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true)
      else setLoading(true)
      setError(null)

      const startDate = getStartDateForRange(range)
      const resp = await getETFVolume(startDate)

      if (resp.success && resp.data) {
        setRecords(resp.data)
      } else {
        setError(resp.error || '获取数据失败')
      }
    } catch (e: any) {
      setError(e.message || '请求失败')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchData(dateRange)
  }, [fetchData, dateRange])

  // Cleanup backfill polling on unmount
  useEffect(() => {
    return () => {
      if (backfillTimerRef.current) clearInterval(backfillTimerRef.current)
    }
  }, [])

  // --- Derived data ---

  // Pivot flat records into chart data: one object per date
  const chartData: ChartDataPoint[] = (() => {
    const dateMap = new Map<string, ChartDataPoint>()
    for (const r of records) {
      const d = r.stat_date
      if (!dateMap.has(d)) {
        const point: ChartDataPoint = { date: d }
        dateMap.set(d, point)
      }
      const point = dateMap.get(d)!
      point[r.sec_code] = r.tot_vol / 10000 // 万元 -> 亿元
    }
    const result = Array.from(dateMap.values())
    result.sort((a, b) => (a.date as string).localeCompare(b.date as string))
    return result
  })()

  // Unique dates
  const uniqueDates = [...new Set(records.map((r) => r.stat_date))].sort()
  const latestDate = uniqueDates.length > 0 ? uniqueDates[uniqueDates.length - 1] : null
  const prevDate = uniqueDates.length > 1 ? uniqueDates[uniqueDates.length - 2] : null

  // Latest day records for watchlist ETFs
  const latestDayRecords = records
    .filter((r) => r.stat_date === latestDate && etfCodes.includes(r.sec_code))
    .sort((a, b) => b.tot_vol - a.tot_vol)

  // Previous day records
  const prevDayRecords = records.filter((r) => r.stat_date === prevDate && etfCodes.includes(r.sec_code))

  // Total scale (latest day, 亿元)
  const totalLatest = latestDayRecords.reduce((sum, r) => sum + r.tot_vol, 0) / 10000
  const totalPrev = prevDayRecords.reduce((sum, r) => sum + r.tot_vol, 0) / 10000
  const dayChange = totalPrev > 0 ? ((totalLatest - totalPrev) / totalPrev) * 100 : null

  // --- Handlers ---

  const [syncMsg, setSyncMsg] = useState<{ type: 'ok' | 'warn'; text: string } | null>(null)

  const handleSync = async () => {
    try {
      setSyncing(true)
      setSyncMsg(null)
      const resp = await syncETFData()
      if (resp.need_backfill) {
        setSyncMsg({ type: 'warn', text: resp.message })
      } else if (resp.success) {
        setSyncMsg({ type: 'ok', text: resp.message })
        await fetchData(dateRange, true)
        setTimeout(() => setSyncMsg(null), 5000)
      } else {
        setSyncMsg({ type: 'warn', text: resp.message || '同步失败' })
      }
    } catch (e: any) {
      setError(e.message || '同步失败')
    } finally {
      setSyncing(false)
    }
  }

  const pollBackfillStatus = useCallback(() => {
    if (backfillTimerRef.current) clearInterval(backfillTimerRef.current)
    backfillTimerRef.current = setInterval(async () => {
      try {
        const status = await getBackfillStatus()
        if (status.status === 'running') {
          setBackfillProgress({
            progress: status.progress || 0,
            total: status.total || 0,
            message: status.message || '回填中...',
          })
        } else {
          // completed, error, or idle
          setBackfilling(false)
          setBackfillProgress(null)
          if (backfillTimerRef.current) clearInterval(backfillTimerRef.current)
          backfillTimerRef.current = null
          // Refresh data after backfill completes
          if (status.status === 'completed') {
            fetchData(dateRange, true)
          }
        }
      } catch {
        // ignore polling errors
      }
    }, 3000)
  }, [fetchData, dateRange])

  // On mount: check if backfill is already running, resume progress display
  useEffect(() => {
    const checkRunning = async () => {
      try {
        const status = await getBackfillStatus()
        if (status.status === 'running') {
          setBackfilling(true)
          setBackfillProgress({
            progress: status.progress || 0,
            total: status.total || 0,
            message: status.message || '回填中...',
          })
          pollBackfillStatus()
        }
      } catch {
        // ignore
      }
    }
    checkRunning()
  }, [pollBackfillStatus])

  const handleBackfill = async () => {
    try {
      setBackfilling(true)
      setShowBackfillPanel(false)
      setBackfillProgress({ progress: 0, total: 0, message: '启动回填...' })
      await backfillETF(backfillStartDate, backfillEndDate, backfillForce)
      pollBackfillStatus()
    } catch (e: any) {
      setBackfilling(false)
      setBackfillProgress(null)
      setError(e.message || '回填失败')
    }
  }

  const handleCheckGaps = async () => {
    try {
      setGapChecking(true)
      const resp = await getETFDateGaps(10)
      if (resp.success) {
        setGaps(resp.gaps || [])
      }
    } catch (e: any) {
      setError(e.message || '检测失败')
    } finally {
      setGapChecking(false)
    }
  }

  const handleFixAllGaps = async () => {
    if (!gaps || gaps.length === 0) return
    try {
      setGapFixing(true)
      // 逐个断连区间触发回填（串行，前一个完成再下一个）
      for (const gap of gaps) {
        // gap_start 是断连前最后一天，从下一天开始补
        const start = new Date(gap.gap_start)
        start.setDate(start.getDate() + 1)
        const startStr = start.toISOString().slice(0, 10)
        // gap_end 是断连后第一天，补到前一天
        const end = new Date(gap.gap_end)
        end.setDate(end.getDate() - 1)
        const endStr = end.toISOString().slice(0, 10)
        if (startStr <= endStr) {
          await backfillETF(startStr, endStr)
          // 等回填完成
          let done = false
          while (!done) {
            await new Promise((r) => setTimeout(r, 2000))
            const status = await getBackfillStatus()
            if (status.status !== 'running') done = true
          }
        }
      }
      // 修复完成后重新检测和刷新
      setGaps(null)
      await handleCheckGaps()
      await fetchData(dateRange, true)
    } catch (e: any) {
      setError(e.message || '修复失败')
    } finally {
      setGapFixing(false)
    }
  }

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range)
  }

  const toggleLine = (code: string) => {
    setHiddenLines((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  // --- Watchlist management handlers ---

  const handleAddETF = async () => {
    const code = newCode.trim()
    if (code.length !== 6 || !/^\d{6}$/.test(code)) return
    try {
      setAddingCode(true)
      setAddResult(null)
      const resp = await addETFToWatchlist(code, newName.trim() || undefined)
      setNewCode('')
      setNewName('')
      await fetchWatchlist()
      if (resp.warning) {
        setAddResult({ type: 'warn', text: resp.warning })
      } else {
        setAddResult({ type: 'ok', text: `已添加 ${code}${resp.synced_count ? `，已同步 ${resp.synced_count} 条数据` : ''}` })
        await fetchData(dateRange, true)
        setTimeout(() => setAddResult(null), 5000)
      }
    } catch (e: any) {
      setAddResult({ type: 'warn', text: e.message || '添加失败' })
    } finally {
      setAddingCode(false)
    }
  }

  const handleRemoveETF = async (secCode: string) => {
    const item = watchlist.find((w) => w.sec_code === secCode)
    const name = item ? `${secCode} ${item.sec_name}` : secCode
    if (!window.confirm(`确定要移除 ${name} 吗？`)) return
    try {
      await removeETFFromWatchlist(secCode)
      await fetchWatchlist()
    } catch (e: any) {
      setError(e.message || '移除失败')
    }
  }

  const handleToggleEnabled = async (secCode: string, currentEnabled: number) => {
    const newEnabled = currentEnabled === 1 ? 0 : 1
    try {
      await updateETFWatchlistItem(secCode, { enabled: newEnabled })
      setWatchlist((prev) =>
        prev.map((w) => (w.sec_code === secCode ? { ...w, enabled: newEnabled } : w))
      )
      // Also update session hiddenLines to match
      setHiddenLines((prev) => {
        const next = new Set(prev)
        if (newEnabled === 0) next.add(secCode)
        else next.delete(secCode)
        return next
      })
    } catch (e: any) {
      setError(e.message || '更新失败')
    }
  }

  const handleChangeColor = async (secCode: string, color: string) => {
    try {
      await updateETFWatchlistItem(secCode, { color })
      setWatchlist((prev) =>
        prev.map((w) => (w.sec_code === secCode ? { ...w, color } : w))
      )
      setColorPickerCode(null)
    } catch (e: any) {
      setError(e.message || '更新颜色失败')
    }
  }

  // --- Loading state ---

  if (loading && watchlistLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center">
              <Database className="w-8 h-8 text-amber-400 animate-pulse" />
            </div>
            <div
              className="absolute -inset-2 rounded-2xl border border-amber-500/20 animate-ping"
              style={{ animationDuration: '2s' }}
            />
          </div>
          <p className="text-gray-400 text-sm">正在获取ETF规模数据...</p>
        </div>
      </div>
    )
  }

  // --- Error state (only show when no data at all) ---

  if (error && records.length === 0) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/20 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">数据获取失败</h3>
            <p className="text-sm text-gray-400 mb-4">{error}</p>
          </div>
          <button
            onClick={() => fetchData(dateRange)}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500/20 text-amber-400 rounded-xl hover:bg-amber-500/30 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            重新获取
          </button>
        </div>
      </div>
    )
  }

  // --- Stat cards ---

  const stats = [
    {
      label: '数据日期',
      value: latestDate || '-',
      sub: null,
      trend: null,
      icon: Calendar,
    },
    {
      label: '总规模',
      value: totalLatest > 0 ? formatYi(totalLatest) : '-',
      sub: null,
      trend: null,
      icon: Database,
    },
    {
      label: '日环比',
      value: dayChange !== null ? `${dayChange >= 0 ? '+' : ''}${dayChange.toFixed(2)}%` : '-',
      sub: null,
      trend: dayChange !== null ? (dayChange >= 0 ? 'up' : 'down') : null,
      icon: dayChange !== null && dayChange >= 0 ? TrendingUp : TrendingDown,
    },
    {
      label: '数据天数',
      value: uniqueDates.length > 0 ? `${uniqueDates.length} 天` : '-',
      sub: null,
      trend: null,
      icon: BarChart3,
    },
  ]

  const dateRangeButtons: { label: string; value: DateRange }[] = [
    { label: '近1月', value: '1m' },
    { label: '近3月', value: '3m' },
    { label: '近6月', value: '6m' },
    { label: '近1年', value: '1y' },
    { label: '全部', value: 'all' },
  ]

  // --- Empty data ---

  const hasData = records.length > 0

  // --- Render ---

  return (
    <div className="space-y-6">
      {/* Manager drawer backdrop */}
      <AnimatePresence>
        {showManager && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={() => setShowManager(false)}
          />
        )}
      </AnimatePresence>

      {/* Manager drawer panel */}
      <motion.div
        initial={{ x: 400 }}
        animate={{ x: showManager ? 0 : 400 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed right-0 top-0 h-full w-96 bg-[#1a1f2e]/95 backdrop-blur-xl border-l border-white/10 z-50 flex flex-col shadow-2xl shadow-black/50"
        style={{ pointerEvents: showManager ? 'auto' : 'none' }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2.5">
            <Settings className="w-5 h-5 text-amber-400" />
            ETF 关注管理
          </h2>
          <button
            onClick={() => setShowManager(false)}
            className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Watchlist items */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
          {watchlist.length === 0 && !watchlistLoading && (
            <div className="text-center py-12">
              <Database className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">暂无关注ETF</p>
              <p className="text-xs text-gray-600 mt-1">在下方输入代码添加</p>
            </div>
          )}
          {watchlist.map((item) => (
            <div
              key={item.sec_code}
              className="group flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
            >
              {/* Color dot (clickable for color picker) */}
              <div className="relative">
                <button
                  onClick={() => setColorPickerCode(colorPickerCode === item.sec_code ? null : item.sec_code)}
                  className="w-3 h-3 rounded-full ring-2 ring-white/10 hover:ring-white/30 transition-all cursor-pointer flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                  title="更换颜色"
                />
                {/* Color picker dropdown */}
                <AnimatePresence>
                  {colorPickerCode === item.sec_code && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="absolute left-0 top-full mt-2 p-2.5 bg-[#1e2436] border border-white/10 rounded-xl shadow-xl shadow-black/40 z-10 w-[180px]"
                    >
                      <div className="grid grid-cols-6 gap-1.5">
                        {COLOR_PALETTE.map((c) => (
                          <button
                            key={c}
                            onClick={() => handleChangeColor(item.sec_code, c)}
                            className={`w-6 h-6 rounded-full hover:scale-110 transition-transform ring-1 ${
                              item.color === c ? 'ring-white ring-2 scale-110' : 'ring-white/10'
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Code */}
              <span className="text-sm text-gray-300 font-mono w-16 flex-shrink-0">
                {item.sec_code}
              </span>

              {/* Name */}
              <span className="text-sm text-gray-400 truncate flex-1 min-w-0">
                {item.sec_name || '-'}
              </span>

              {/* Eye toggle */}
              <button
                onClick={() => handleToggleEnabled(item.sec_code, item.enabled)}
                className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
                  item.enabled === 1
                    ? 'text-amber-400 hover:bg-amber-500/15'
                    : 'text-gray-600 hover:bg-white/5'
                }`}
                title={item.enabled === 1 ? '隐藏此ETF' : '显示此ETF'}
              >
                {item.enabled === 1 ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>

              {/* Delete */}
              <button
                onClick={() => handleRemoveETF(item.sec_code)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                title="移除此ETF"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Add section */}
        <div className="border-t border-white/10 px-6 py-5">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
            添加新ETF
          </h3>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={newCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6)
                setNewCode(v)
              }}
              placeholder="6位代码"
              className="w-28 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/40 transition-colors font-mono"
            />
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddETF()
              }}
              placeholder="名称（可选，留空自动查询）"
              className="flex-1 px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/40 transition-colors"
            />
            <button
              onClick={handleAddETF}
              disabled={addingCode || newCode.length !== 6}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-amber-500/15 text-amber-400 rounded-xl hover:bg-amber-500/25 transition-all border border-amber-500/20 hover:border-amber-500/40 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              {addingCode ? '添加中...' : '添加'}
            </button>
          </div>
          <p className="text-xs text-gray-600">
            深交所ETF（如159xxx）请手动填写名称，上交所ETF可自动查询
          </p>
          {addResult && (
            <div className={`mt-2 px-3 py-2 rounded-lg text-xs ${addResult.type === 'warn' ? 'bg-orange-500/10 text-orange-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
              {addResult.text}
            </div>
          )}
        </div>
      </motion.div>

      {/* Title bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Database className="w-5 h-5 text-white" />
            </div>
            上证ETF规模趋势
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-sm text-gray-500">数据来源</span>
            <a
              href="https://www.sse.com.cn/market/funddata/volumn/etfvolumn/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-amber-400/80 hover:text-amber-400 transition-colors"
            >
              上海证券交易所
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Manage ETF button */}
          <button
            onClick={() => setShowManager(true)}
            title="管理ETF关注列表：添加、删除、显示/隐藏ETF"
            className="flex items-center gap-2 px-4 py-2.5 bg-white/5 text-gray-400 rounded-xl hover:bg-amber-500/15 hover:text-amber-400 transition-all border border-white/10 hover:border-amber-500/30"
          >
            <Settings className="w-4 h-4" />
            管理ETF
          </button>
          {/* Sync button */}
          <button
            onClick={handleSync}
            disabled={syncing || refreshing}
            title="智能补齐：自动获取数据库最新日期到今天的数据（≤30天）。超过30天请使用历史回填。"
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/15 text-amber-400 rounded-xl hover:bg-amber-500/25 transition-all border border-amber-500/20 hover:border-amber-500/40 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? '同步中...' : '同步最新'}
          </button>
          {/* Backfill button */}
          <div className="relative">
            <button
              onClick={() => setShowBackfillPanel(!showBackfillPanel)}
              disabled={backfilling}
              title="批量拉取指定日期范围的历史数据，适合首次使用或补齐大段缺失数据。"
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-500/15 text-orange-400 rounded-xl hover:bg-orange-500/25 transition-all border border-orange-500/20 hover:border-orange-500/40 disabled:opacity-50"
            >
              <Download className={`w-4 h-4 ${backfilling ? 'animate-bounce' : ''}`} />
              {backfilling ? '回填中...' : '历史回填'}
            </button>
            {showBackfillPanel && !backfilling && (
              <div className="absolute right-0 top-full mt-2 w-72 glass-card p-4 z-50 shadow-xl shadow-black/30">
                <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-orange-400" />
                  选择回填日期范围
                </h4>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="date"
                    value={backfillStartDate}
                    onChange={(e) => setBackfillStartDate(e.target.value)}
                    max={backfillEndDate}
                    className="flex-1 px-3 py-2 bg-dark border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-orange-500/50 transition-colors"
                  />
                  <span className="text-xs text-gray-500">至</span>
                  <input
                    type="date"
                    value={backfillEndDate}
                    onChange={(e) => setBackfillEndDate(e.target.value)}
                    min={backfillStartDate}
                    max={new Date().toISOString().slice(0, 10)}
                    className="flex-1 px-3 py-2 bg-dark border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-orange-500/50 transition-colors"
                  />
                </div>
                <div className="flex gap-2 mb-3">
                  {[
                    { label: '近3月', days: 90 },
                    { label: '近6月', days: 180 },
                    { label: '近1年', days: 365 },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      onClick={() => {
                        const d = new Date()
                        d.setDate(d.getDate() - opt.days)
                        setBackfillStartDate(d.toISOString().slice(0, 10))
                      }}
                      className="flex-1 px-2 py-1.5 text-xs bg-white/5 text-gray-400 rounded-lg hover:bg-orange-500/15 hover:text-orange-400 transition-colors"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 mb-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={backfillForce}
                    onChange={(e) => setBackfillForce(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-white/20 bg-dark text-orange-500 focus:ring-orange-500/30 focus:ring-offset-0"
                  />
                  <span className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                    强制覆盖（重新抓取已有数据）
                  </span>
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowBackfillPanel(false)}
                    className="flex-1 px-3 py-2 text-xs text-gray-400 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleBackfill}
                    className="flex-1 px-3 py-2 text-xs text-white bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg hover:from-orange-600 hover:to-amber-600 transition-colors font-medium"
                  >
                    开始回填
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Gap detection button */}
          <button
            onClick={handleCheckGaps}
            disabled={gapChecking || gapFixing}
            title="检测数据中超过10天的日期断连，支持一键修复。"
            className="flex items-center gap-2 px-4 py-2.5 bg-violet-500/15 text-violet-400 rounded-xl hover:bg-violet-500/25 transition-all border border-violet-500/20 hover:border-violet-500/40 disabled:opacity-50"
          >
            <SearchCheck className={`w-4 h-4 ${gapChecking ? 'animate-spin' : ''}`} />
            {gapChecking ? '检测中...' : '检测断连'}
          </button>
        </div>
      </motion.div>

      {/* Gap detection results */}
      {gaps !== null && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-5 border-l-4 border-violet-500/60"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <SearchCheck className="w-4 h-4 text-violet-400" />
              断连检测结果
            </h3>
            <div className="flex items-center gap-2">
              {gaps.length > 0 && (
                <button
                  onClick={handleFixAllGaps}
                  disabled={gapFixing || backfilling}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-violet-500/20 text-violet-400 rounded-lg hover:bg-violet-500/30 transition-colors disabled:opacity-50 font-medium"
                >
                  <Wrench className={`w-3.5 h-3.5 ${gapFixing ? 'animate-spin' : ''}`} />
                  {gapFixing ? '修复中...' : `一键修复 (${gaps.length}处)`}
                </button>
              )}
              <button
                onClick={() => setGaps(null)}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
          {gaps.length === 0 ? (
            <p className="text-sm text-emerald-400">数据连续，无断连。</p>
          ) : (
            <div className="space-y-2">
              {gaps.map((gap, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between px-3 py-2 bg-white/[0.03] rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded-full bg-violet-500/20 text-violet-400 text-xs flex items-center justify-center font-mono">
                      {i + 1}
                    </span>
                    <span className="text-sm text-gray-300 font-mono">
                      {gap.gap_start}
                    </span>
                    <span className="text-xs text-gray-500">-&gt;</span>
                    <span className="text-sm text-gray-300 font-mono">
                      {gap.gap_end}
                    </span>
                  </div>
                  <span className="text-xs text-red-400 font-medium">
                    缺失 {gap.gap_days} 天
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Sync message */}
      {syncMsg && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className={`glass-card p-3 border-l-4 ${syncMsg.type === 'warn' ? 'border-orange-500/60' : 'border-emerald-500/60'}`}
        >
          <p className={`text-sm ${syncMsg.type === 'warn' ? 'text-orange-300' : 'text-emerald-300'}`}>
            {syncMsg.text}
          </p>
        </motion.div>
      )}

      {/* Backfill progress */}
      {backfilling && backfillProgress && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="glass-card p-4 border-l-4 border-orange-500/60"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-orange-300 font-medium">{backfillProgress.message}</span>
            {backfillProgress.total > 0 && (
              <span className="text-xs text-gray-400">
                {backfillProgress.progress} / {backfillProgress.total}
              </span>
            )}
          </div>
          {backfillProgress.total > 0 && (
            <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min((backfillProgress.progress / backfillProgress.total) * 100, 100)}%`,
                }}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => {
          const Icon = s.icon
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="glass-card p-5 border-l-4 border-amber-500/60 hover:border-amber-400 transition-colors group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                    {s.label}
                  </p>
                  <p
                    className={`text-xl font-bold mt-1.5 ${
                      s.trend === 'up'
                        ? 'text-emerald-400'
                        : s.trend === 'down'
                          ? 'text-red-400'
                          : 'text-white'
                    }`}
                  >
                    {s.value}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <Icon className="w-5 h-5 text-amber-400/70" />
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Empty watchlist notice */}
      {watchlist.length === 0 && !watchlistLoading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-12 text-center"
        >
          <Settings className="w-12 h-12 text-amber-400/40 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">尚未添加关注ETF</h3>
          <p className="text-sm text-gray-400 mb-6">
            请点击右上角"管理ETF"按钮，添加需要跟踪的ETF基金。
          </p>
          <button
            onClick={() => setShowManager(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500/20 text-amber-400 rounded-xl hover:bg-amber-500/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            添加ETF
          </button>
        </motion.div>
      )}

      {/* Empty data state (watchlist exists but no volume data) */}
      {!hasData && watchlist.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-12 text-center"
        >
          <Database className="w-12 h-12 text-amber-400/40 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">暂无数据</h3>
          <p className="text-sm text-gray-400 mb-6">
            请点击"历史回填"按钮获取历史数据，或点击"同步最新"获取当日数据。
          </p>
        </motion.div>
      )}

      {/* Trend chart */}
      {hasData && chartData.length > 0 && etfCodes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="glass-card p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-amber-400" />
              规模趋势（亿元）
            </h2>
            {/* Date range filters */}
            <div className="flex items-center gap-1">
              {dateRangeButtons.map((btn) => (
                <button
                  key={btn.value}
                  onClick={() => handleDateRangeChange(btn.value)}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                    dateRange === btn.value
                      ? 'bg-amber-500/25 text-amber-300 border border-amber-500/40'
                      : 'text-gray-500 hover:text-gray-300 hover:bg-white/5 border border-transparent'
                  }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
            {etfCodes.map((code) => {
              const cfg = etfConfig[code]
              const isHidden = hiddenLines.has(code)
              return (
                <button
                  key={code}
                  onClick={() => toggleLine(code)}
                  className={`flex items-center gap-1.5 text-xs transition-all ${
                    isHidden ? 'opacity-30' : 'opacity-100'
                  } hover:opacity-80`}
                >
                  <div
                    className="w-3 h-1.5 rounded-full"
                    style={{ backgroundColor: cfg.color }}
                  />
                  <span className="text-gray-400">{code} {cfg.name}</span>
                </button>
              )
            })}
          </div>

          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="date"
                  stroke="#475569"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke="#475569"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={60}
                  tickFormatter={(v: any) => `${Number(v).toFixed(0)}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    fontSize: '13px',
                    color: '#e2e8f0',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                  }}
                  formatter={(value: any, name: any) => {
                    const code = String(name)
                    const cfg = etfConfig[code]
                    const label = cfg ? `${code} ${cfg.name}` : code
                    return [`${Number(value).toFixed(2)} 亿`, label]
                  }}
                  labelFormatter={(label: any) => `日期: ${label}`}
                />
                <Legend content={() => null} />
                {etfCodes.map((code) => (
                  <Line
                    key={code}
                    type="monotone"
                    dataKey={code}
                    stroke={etfConfig[code].color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2 }}
                    hide={hiddenLines.has(code)}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Detail table */}
      {hasData && latestDayRecords.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="glass-card overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
              <Database className="w-4 h-4 text-amber-400" />
              最新规模明细（{latestDate}）
            </h2>
            <span className="text-xs text-gray-500">{latestDayRecords.length} 只ETF</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.03]">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    代码
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    名称
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    类型
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    规模(亿元)
                  </th>
                </tr>
              </thead>
              <tbody>
                {latestDayRecords.map((r, i) => {
                  const cfg = etfConfig[r.sec_code]
                  return (
                    <motion.tr
                      key={r.sec_code}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 + i * 0.08 }}
                      className="border-b border-white/[0.04] hover:bg-amber-500/[0.04] transition-colors"
                    >
                      <td className="px-4 py-3 text-gray-300 font-mono text-xs">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: cfg?.color || '#6B7280' }}
                          />
                          {r.sec_code}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-xs">{r.sec_name}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{r.etf_type}</td>
                      <td className="px-4 py-3 text-right text-amber-300 font-mono text-xs font-medium">
                        {(r.tot_vol / 10000).toFixed(2)}
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  )
}
